import * as Sentry from "@sentry/react";
import {
  useMutation,
  useQueryClient,
  useQuery,
  type Query,
  type UseQueryResult,
} from "@tanstack/react-query";
import { compact } from "lodash";
import { enqueueSnackbar } from "notistack";
import z from "zod";

import { withJWTClient } from "./connectClient";

import {
  audienceFiltersSchema,
  audienceSchema,
  audienceUserSchema,
  type Audience,
  type AudienceCreate,
  type AudiencesSearch,
  type AudienceMembersSearchInfo,
  audienceCountSchema,
  audienceReachSchema,
  audienceListSearchSchema,
  audienceMemberSearchSchema,
  type AudienceCount,
  type AudienceProcessingState,
  type AudienceReach,
  audienceExportJobSchema,
  filtersEstimateSchema,
  type AudienceFilters,
  type FiltersEstimate,
  type AudienceExportsSearch,
  type UserAudiencesLookup,
  type UserAudiencesResponse,
  userAudiencesResponseSchema,
} from "@/features/Audiences/types";
import { exportResponseSchema } from "@/features/ListsV2/types";
import { getBackendErrorMessage } from "@/utils/backendError";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

const API_BASE_URL: string = import.meta.env.DEV
  ? "/audiences"
  : (import.meta.env.VITE_AUDIENCES_API_URL as string) || "";

// One unparseable row must not blank every audience list and picker.
const reportedInvalidFilters = new Set<string>();
const audienceListItemSchema = audienceSchema.extend({
  filters: audienceFiltersSchema.nullable().catch(({ error, input }) => {
    // Filter values can hold customer PII; only the ZodError goes to Sentry.
    const key = JSON.stringify(input) ?? "";
    if (!reportedInvalidFilters.has(key)) {
      reportedInvalidFilters.add(key);
      Sentry.captureException(error);
    }
    return null;
  }),
});
export type AudienceListItem = z.infer<typeof audienceListItemSchema>;

const getAudiencesResponseSchema = z.object({
  data: z.array(audienceListItemSchema),
  pagination: z.object({
    nextCursor: z.string(),
    totalCount: z.coerce.number().gte(0),
  }),
});
export type GetAudiencesResponse = z.infer<typeof getAudiencesResponseSchema>;

export const AUDIENCE_POLL_INTERVAL_MS = 5_000;

export const isProcessingState = (state: AudienceProcessingState | undefined) =>
  state === "AUDIENCE_PROCESSING_STATE_PENDING" ||
  state === "AUDIENCE_PROCESSING_STATE_PROCESSING";

export const hasProcessingAudience = (data: GetAudiencesResponse | undefined) =>
  (data?.data ?? []).some((audience) => isProcessingState(audience.state));

const audienceQueryKey = (key: string, siteId: string, params: string[]) => {
  return compact([key, siteId, ...params]);
};

export const audienceExportsQueryKey = (siteId: string) =>
  audienceQueryKey("audienceexports", siteId, []);

const getAudiences = async (
  siteId: string,
  params?: Partial<AudiencesSearch>,
): Promise<GetAudiencesResponse> => {
  const queryParams = new URLSearchParams();

  if (params?.page) {
    queryParams.append("pagination.page", String(params.page));
  }
  if (params?.pageSize) {
    queryParams.append("pagination.limit", String(params.pageSize));
  }
  if (params?.search) {
    queryParams.append("search", params.search.trim().toLowerCase());
  }

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/${siteId}/audience_builder${
    queryString ? `?${queryString}` : ""
  }`;

  const response = await withJWTClient((client) => client.get(url));
  try {
    return getAudiencesResponseSchema.parse(response.data);
  } catch (err) {
    throw new Error(
      `Failed to fetch audiences: ${getBackendErrorMessage(err)}`,
    );
  }
};

export const getAudiencesQueryOptions = (
  siteId: string,
  params?: Partial<AudiencesSearch>,
) => {
  const s = audienceListSearchSchema.parse(params);
  return {
    queryKey: audienceQueryKey("audiences", siteId, [
      s.search ?? "",
      String(s.page),
      String(s.pageSize),
    ]),
    queryFn: () => getAudiences(siteId, params),
    enabled: !!siteId,
    staleTime: 30_000,
    refetchInterval: (query: Query<GetAudiencesResponse>) =>
      hasProcessingAudience(query.state.data)
        ? AUDIENCE_POLL_INTERVAL_MS
        : false,
  };
};

const getAudienceMembersResponseSchema = z.object({
  data: z.array(audienceUserSchema),
  nextCursor: z.string(),
});
export type GetAudienceMembersResponse = z.infer<
  typeof getAudienceMembersResponseSchema
>;

const getAudienceMembers = async (
  siteId: string,
  audienceId: string,
  params?: Partial<AudienceMembersSearchInfo>,
): Promise<GetAudienceMembersResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.nextCursor != null) {
    queryParams.append("nextCursor", String(params.nextCursor));
  }
  if (params?.search) {
    queryParams.append("search", params.search);
  }
  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/${siteId}/audience_builder/${audienceId}/members${
    queryString ? `?${queryString}` : ""
  }`;
  const response = await withJWTClient((client) => client.get(url));
  try {
    return getAudienceMembersResponseSchema.parse(response.data);
  } catch (err) {
    throw new Error(
      `Failed to fetch audience members: ${getBackendErrorMessage(err)}`,
    );
  }
};

export const getAudienceMembersQueryOptions = (
  siteId: string,
  audienceId: string,
  params?: Partial<AudienceMembersSearchInfo>,
) => {
  const s = audienceMemberSearchSchema.parse(params);
  return {
    queryKey: audienceQueryKey("audiencemembers", siteId, [
      audienceId,
      s.nextCursor ?? "",
      s.search ?? "",
    ]),
    params,
    queryFn: () => getAudienceMembers(siteId, audienceId, params),
    enabled: !!siteId && !!audienceId,
    staleTime: 30_000,
  };
};

const getAudience = async (siteId: string, audienceId: string) => {
  const url = `${API_BASE_URL}/${siteId}/audience_builder/${audienceId}`;
  const response = await withJWTClient((client) => client.get(url));
  try {
    return audienceSchema.parse(response.data);
  } catch (err) {
    throw new Error(`Failed to fetch audience: ${getBackendErrorMessage(err)}`);
  }
};

export const getAudienceQueryOptions = (
  siteId: string,
  audienceId: string | null,
) => ({
  queryKey: audienceQueryKey("audience", siteId, [audienceId ?? ""]),
  queryFn: () => (audienceId ? getAudience(siteId, audienceId) : null),
  enabled: !!siteId && !!audienceId,
  staleTime: 30_000,
});

export const useGetAudience = (siteId: string, audienceId: string) => {
  return useQuery({
    queryKey: audienceQueryKey("audience", siteId, [audienceId]),
    queryFn: () => getAudience(siteId, audienceId),
    enabled: !!siteId && !!audienceId,
    staleTime: 30_000,
  });
};

/**
 * Hydrate multiple audiences for chip labels in one React Query entry.
 * Backend has no batch-by-id endpoint, so requests still run in parallel via
 * Promise.allSettled — callers avoid N separate useQueries subscriptions.
 */
export const getAudiencesByIdsQueryOptions = (
  siteId: string,
  audienceIds: string[],
) => {
  const sortedIds = [...new Set(audienceIds.filter(Boolean))].sort();
  return {
    queryKey: audienceQueryKey("audiencesByIds", siteId, sortedIds),
    queryFn: async (): Promise<Audience[]> => {
      const settled = await Promise.allSettled(
        sortedIds.map((audienceId) => getAudience(siteId, audienceId)),
      );
      return settled.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      );
    },
    enabled: !!siteId && sortedIds.length > 0,
    staleTime: 30_000,
  };
};

export function useGetAudiencesByIds(siteId: string, audienceIds: string[]) {
  return useQuery(getAudiencesByIdsQueryOptions(siteId, audienceIds));
}

const userAudienceIdsResponseSchema = z.object({
  audienceIds: z.array(z.string()),
});

export type UserAudienceIdsResponse = z.infer<
  typeof userAudienceIdsResponseSchema
>;

const getUserAudienceIds = async (
  siteId: string,
  userId: string,
): Promise<UserAudienceIdsResponse> => {
  const url = `${API_BASE_URL}/${siteId}/users/${userId}/audiences`;
  const response = await withJWTClient((client) => client.get(url));
  try {
    return userAudienceIdsResponseSchema.parse(response.data);
  } catch (err) {
    throw new Error(
      `Failed to fetch audiences for user: ${getBackendErrorMessage(err)}`,
    );
  }
};

export const getUserAudienceIdsQueryOptions = (
  siteId: string,
  userId: string,
) => ({
  queryKey: audienceQueryKey("userAudiences", siteId, [userId]),
  queryFn: () => getUserAudienceIds(siteId, userId),
  enabled: !!siteId && !!userId,
  staleTime: 30_000,
});

export function useGetUserAudienceIds(siteId: string, userId: string) {
  return useQuery({
    queryKey: audienceQueryKey("userAudiences", siteId, [userId]),
    queryFn: () => getUserAudienceIds(siteId, userId),
    enabled: !!siteId && !!userId,
  });
}

const getAudienceCount = async (
  siteId: string,
  audienceIds: string[],
): Promise<AudienceCount> => {
  const queryParams = new URLSearchParams();
  for (const id of audienceIds) {
    queryParams.append("audienceIds[]", id);
  }
  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/${siteId}/audience_builder/counts?${queryString}`;
  const response = await withJWTClient((client) => client.get(url));
  return audienceCountSchema.parse(response.data);
};

export const getAudienceMemberCountQueryOptions = (
  siteId: string,
  audienceIds: string[],
) => ({
  queryKey: audienceQueryKey(
    "audiencemembercount",
    siteId,
    [...audienceIds].sort(),
  ),
  queryFn: () => getAudienceCount(siteId, audienceIds),
  enabled: !!siteId && audienceIds.length > 0,
  staleTime: 30_000,
});

export function useGetAudienceMemberCount(
  siteId: string,
  audienceIds: string[],
) {
  return useQuery({
    queryKey: audienceQueryKey(
      "audiencemembercount",
      siteId,
      [...audienceIds].sort(),
    ),
    queryFn: () => getAudienceCount(siteId, audienceIds),
    enabled: !!siteId && audienceIds.length > 0,
    staleTime: 30_000,
  });
}

/**
 * Optional channel gate for `/reach`: restricts the estimate to profiles
 * eligible on that channel. The backend accepts only these two enum names;
 * omitted → ungated (legacy behavior).
 */
export type AudienceReachMedium = "MEDIUM_TYPE_EMAIL" | "MEDIUM_TYPE_SMS";

const getAudienceReach = async (
  siteId: string,
  audienceIds: string[],
  reachOptimization: boolean,
  excludedAudienceIds: string[] = [],
  medium?: AudienceReachMedium,
): Promise<AudienceReach> => {
  const queryParams = new URLSearchParams();
  // `/reach` uses `collectionFormat: multi` (repeated `audienceIds`, no `[]`
  // suffix), unlike `/counts` which expects `audienceIds[]`.
  for (const id of audienceIds) {
    queryParams.append("audienceIds", id);
  }
  // Excluded ("do not send to") audiences are subtracted from the union count
  // server-side; they use the same repeated-param format as `audienceIds`.
  for (const id of excludedAudienceIds) {
    queryParams.append("excludedAudienceIds", id);
  }
  if (reachOptimization) {
    queryParams.append("reachOptimization", "true");
  }
  if (medium) {
    queryParams.append("medium", medium);
  }
  const url = `${API_BASE_URL}/${siteId}/audience_builder/reach?${queryParams.toString()}`;
  const response = await withJWTClient((client) => client.get(url));
  return audienceReachSchema.parse(response.data);
};

export const getAudienceReachQueryOptions = (
  siteId: string,
  audienceIds: string[],
  reachOptimization: boolean,
  excludedAudienceIds: string[] = [],
  medium?: AudienceReachMedium,
) => ({
  // Nest the two id arrays as distinct key segments (rather than flattening
  // them with a static "excluded" separator) so no id value can be confused
  // for the separator. Each list is sorted to keep the key deterministic.
  // `medium` is a key segment too, so switching channel refetches.
  queryKey: [
    ...audienceQueryKey("audiencereach", siteId, [
      reachOptimization ? "reachOptimization" : "",
      medium ?? "",
    ]),
    { audienceIds: [...audienceIds].sort() },
    { excludedAudienceIds: [...excludedAudienceIds].sort() },
  ],
  queryFn: () =>
    getAudienceReach(
      siteId,
      audienceIds,
      reachOptimization,
      excludedAudienceIds,
      medium,
    ),
  enabled: !!siteId && audienceIds.length > 0,
  staleTime: 30_000,
});

export function useGetAudienceReach(
  siteId: string,
  audienceIds: string[],
  reachOptimization: boolean,
  excludedAudienceIds: string[] = [],
  medium?: AudienceReachMedium,
) {
  return useQuery(
    getAudienceReachQueryOptions(
      siteId,
      audienceIds,
      reachOptimization,
      excludedAudienceIds,
      medium,
    ),
  );
}

const getAudienceExportsResponseSchema = z.object({
  data: z.array(audienceExportJobSchema),
  pagination: z.object({
    totalCount: z.coerce.number().gte(0),
  }),
});
export type GetAudienceExportsResponse = z.infer<
  typeof getAudienceExportsResponseSchema
>;

const getAudienceExports = async (
  siteId: string,
  params?: Partial<AudienceExportsSearch>,
): Promise<GetAudienceExportsResponse> => {
  const url = `${API_BASE_URL}/${siteId}/audience_builder/exports`;

  const body: Record<string, unknown> = {
    sources: ["EXPORT_SOURCE_AUDIENCE", "EXPORT_SOURCE_LIST"],
    sort: { field: "SORT_FIELD_CREATED_AT", order: "SORT_ORDER_DESC" },
    pagination: {
      limit: params?.pageSize ?? 20,
      page: params?.page ?? 1,
    },
  };

  if (params?.search) {
    body.filter = {
      condition: {
        field: "FILTER_FIELD_NAME",
        operator: "FILTER_OPERATOR_CONTAINS",
        value: { string_value: params.search.toLowerCase() },
      },
    };
  }

  const response = await withJWTClient((client) => client.post(url, body));
  try {
    return getAudienceExportsResponseSchema.parse(response.data);
  } catch (err) {
    throw new Error(
      `Failed to fetch audience exports: ${getBackendErrorMessage(err)}`,
    );
  }
};

export const getAudienceExportsQueryOptions = (
  siteId: string,
  params?: Partial<AudienceExportsSearch>,
) => ({
  queryKey: audienceQueryKey("audienceexports", siteId, [
    params?.search?.trim().toLowerCase() ?? "",
    String(params?.page ?? 1),
    String(params?.pageSize ?? 20),
  ]),
  queryFn: () => getAudienceExports(siteId, params),
  enabled: !!siteId,
  staleTime: 30_000,
});

const getFiltersEstimate = async (
  siteId: string,
  filters: AudienceFilters,
): Promise<FiltersEstimate> => {
  const url = `${API_BASE_URL}/${siteId}/audience_builder/estimate`;
  const response = await withJWTClient((client) =>
    client.post(url, { siteId, filters }),
  );
  return filtersEstimateSchema.parse(response.data);
};

export const getFiltersEstimateQueryOptions = (
  siteId: string,
  filtersKey: string,
  enabled: boolean,
) => ({
  queryKey: audienceQueryKey("audienceestimate", siteId, [filtersKey]),
  queryFn: () =>
    getFiltersEstimate(siteId, JSON.parse(filtersKey) as AudienceFilters),
  enabled: enabled && !!siteId && filtersKey !== "",
  staleTime: 5 * 60_000,
  retry: 1,
});

export const useGetFiltersEstimate = (
  siteId: string,
  filtersKey: string,
  enabled: boolean,
) => useQuery(getFiltersEstimateQueryOptions(siteId, filtersKey, enabled));

export const useCreateNewAudience = (siteId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["createNewAudience", siteId],
    mutationFn: async (data: AudienceCreate) => {
      const url = `${API_BASE_URL}/${data.siteId}/audience_builder`;
      const response = await withJWTClient((client) => client.post(url, data));
      return audienceSchema.parse(response.data);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: audienceQueryKey("audiences", siteId, []),
      });
      void queryClient.invalidateQueries({
        queryKey: audienceQueryKey("audience", siteId, [data.id]),
      });
      enqueueSnackbar("Audience created successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to create audience");
    },
  });
};

export const useExportAudienceMembers = (
  siteId: string,
  audienceId: string,
) => {
  return useMutation({
    mutationKey: ["exportAudienceMembers", siteId, audienceId],
    mutationFn: async (params: { fields: string[]; search?: string }) => {
      const url = `${API_BASE_URL}/${siteId}/audience_builder/${audienceId}/export`;
      const body: Record<string, unknown> = {
        export_metadata: { format: "CSV", fields: params.fields },
      };
      if (params.search) body.search = params.search;
      const response = await withJWTClient((client) => client.post(url, body));
      return exportResponseSchema.parse(response.data);
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to export audience members");
    },
  });
};

export const useUpdateAudience = (siteId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateAudience", siteId],
    mutationFn: async (data: {
      audience: AudienceCreate;
      audienceId: string;
    }) => {
      const url = `${API_BASE_URL}/${data.audience.siteId}/audience_builder/${data.audienceId}`;
      const response = await withJWTClient((client) =>
        client.put(url, data.audience),
      );
      return audienceSchema.parse(response.data);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: audienceQueryKey("audiences", siteId, []),
      });
      queryClient.setQueryData(
        audienceQueryKey("audience", siteId, [data.id]),
        data,
      );
      enqueueSnackbar("Audience updated successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to update audience");
    },
  });
};

export const useDeleteAudience = (siteId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["deleteAudience", siteId],
    mutationFn: async (audienceId: string) => {
      const url = `${API_BASE_URL}/${siteId}/audience_builder/${audienceId}`;
      await withJWTClient((client) => client.delete(url));
    },
    onSuccess: (_data, audienceId) => {
      void queryClient.invalidateQueries({
        queryKey: audienceQueryKey("audiences", siteId, []),
      });
      queryClient.removeQueries({
        queryKey: audienceQueryKey("audience", siteId, [audienceId]),
      });
      enqueueSnackbar("Audience deleted successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to delete audience");
    },
  });
};

/**
 * Downloads an audience export file from a signed URL.
 *
 * This function fetches the file directly from the provided signed URL
 * and triggers a browser download with the specified filename.
 *
 * @param fileUrl - The signed URL to download the file from (from job.result.file_url)
 * @param fileName - The name to save the downloaded file as
 */
export const downloadExport = async (
  fileUrl: string,
  fileName: string,
): Promise<void> => {
  // eslint-disable-next-line demo-guard/no-unguarded-requests -- read-only file download
  const response = await fetch(fileUrl);
  if (!response.ok) {
    throw new Error(`Export download failed: ${response.status}`);
  }
  const blob = await response.blob();

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

async function fetchUserAudiences(
  siteId: string,
  lookup: UserAudiencesLookup,
): Promise<UserAudiencesResponse> {
  const params = new URLSearchParams();
  if (lookup.kind === "email") params.set("email", lookup.value);
  if (lookup.kind === "phone") params.set("phone", lookup.value);
  if (lookup.kind === "userId") params.set("user_id", lookup.value);
  const qs = params.toString();
  const url = `${API_BASE_URL}/${siteId}/users/audiences${qs ? `?${qs}` : ""}`;
  const response = await withJWTClient((client) => client.get(url));
  return userAudiencesResponseSchema.parse(response.data);
}

export function useUserAudiences(
  siteId: string,
  lookup: UserAudiencesLookup,
): UseQueryResult<UserAudiencesResponse, Error> {
  return useQuery<UserAudiencesResponse, Error>({
    queryKey: audienceQueryKey("userAudiences", siteId, [
      lookup.kind,
      lookup.value,
    ]),
    queryFn: () => fetchUserAudiences(siteId, lookup),
    enabled: !!siteId && !!lookup.value.trim(),
    staleTime: 30_000,
  });
}
